import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import PivotTable from "react-pivottable/PivotTable";
import "react-pivottable/pivottable.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import BarChartIcon from "@mui/icons-material/BarChart";
import GridViewIcon from "@mui/icons-material/GridView";
import TableChartIcon from "@mui/icons-material/TableChart";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import StorageIcon from "@mui/icons-material/Storage";
import LayersIcon from "@mui/icons-material/Layers";
import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { fetchProfile } from "../../../utils/fetchProfile";
import Navbar from "../../../Components/Navbar";
import { LEVEL_OPTION } from "../../../utils/constant";

const MODULE_NAME = "pivot";

function extractUnique(data, key) {
  return [...new Set(data.map((d) => d[key]))]
    .filter((v) => v != null && v !== "")
    .sort((a, b) => String(a).localeCompare(String(b)));
}

const fetchTypeProject = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/report_db/db_set`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching DB Sets:", error);
    return [];
  }
};

const fetchFacts = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/fact?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/fact`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Facts:", error);
    return [];
  }
};

const fetchPeriods = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/period?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/period`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Periods:", error);
    return [];
  }
};

const fetchLevels = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/level?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/level`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Levels:", error);
    return [];
  }
};

const fetchMarketBreakdowns = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/market_breakdown?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/market_breakdown`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Market Breakdowns:", error);
    return [];
  }
};

const fetchCategoryMaster = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/category-master`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching category master:", error);
    return [];
  }
};

const fetchBrandMaster = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/brand-master`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching brand master:", error);
    return [];
  }
};

const fetchProductMaster = async (accessToken, params = {}) => {
  try {
    const url = new URL(`${process.env.REACT_APP_API_URL}/api/v1/product-master`);
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });
    const response = await fetch(
      url.toString(),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching product master:", error);
    return [];
  }
};

const fetchRawDataPage = async (accessToken, params = {}) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) =>
          value !== "" &&
          value !== null &&
          (!Array.isArray(value) || value.length > 0)
      )
    );
    Object.keys(filteredParams).forEach((key) => {
      if (Array.isArray(filteredParams[key])) {
        filteredParams[key] = filteredParams[key].join(",");
      } else if (key === "period") {
        filteredParams[key] = String(filteredParams[key]);
      }
    });
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/report_db`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(filteredParams),
      }
    );
    if (response.ok) {
      const result = await response.json();
      const rows = result.data || [];
      const mappedRows = rows.map((row) => ({
        ...row,
        Category: row.Group || row.category || row.Category,
        category: row.Group || row.category || row.Category,
      }));
      return {
        data: mappedRows,
        pagination_info: result.pagination_info || null,
      };
    }
    return { data: [], pagination_info: null };
  } catch (error) {
    console.error("Error fetching raw data:", error);
    return { data: [], pagination_info: null };
  }
};

/* ── Config Template API helpers ── */
const fetchConfigTemplates = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template?module_name=${MODULE_NAME}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const res = await response.json();
      return Array.isArray(res.data) ? res.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching config templates:", error);
    return [];
  }
};

const createConfigTemplate = async (
  accessToken,
  templateName,
  filterPayload
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          module_name: MODULE_NAME,
          template_name: templateName,
          filter_payload: JSON.stringify(filterPayload),
        }),
      }
    );
    if (response.ok) {
      const res = await response.json();
      return res.data || null;
    }
    return null;
  } catch (error) {
    console.error("Error creating config template:", error);
    return null;
  }
};

const updateConfigTemplate = async (
  accessToken,
  templateId,
  templateName,
  filterPayload
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template/${templateId}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template_name: templateName,
          filter_payload: JSON.stringify(filterPayload),
        }),
      }
    );
    if (response.ok) {
      const res = await response.json();
      return res.data || null;
    }
    return null;
  } catch (error) {
    console.error("Error updating config template:", error);
    return null;
  }
};

const deleteConfigTemplate = async (accessToken, templateId) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template/${templateId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.ok;
  } catch (error) {
    console.error("Error deleting config template:", error);
    return false;
  }
};

const iconBtn = (color) => ({
  border: `1.5px solid ${color}`,
  color,
  background: "transparent",
  borderRadius: 7,
  padding: 6,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
});

function LoadingDots() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "#07a3b5",
            animation: "dotPulse 1s infinite alternate",
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </span>
  );
}

function SearchSelect({
  label,
  options,
  value,
  onChange,
  required,
  placeholder = "Semua",
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const filtered = options.filter((o) =>
    String(o).toLowerCase().includes(q.toLowerCase())
  );
  const isErr = required && !value;
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: isErr ? "#e74c3c" : "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        {required && <span style={{ color: "#e74c3c" }}> *</span>}
        {loading && <LoadingDots />}
      </div>
      <div
        onClick={() => !loading && setOpen((p) => !p)}
        style={{
          minHeight: 40,
          padding: "5px 10px",
          border: `1.5px solid ${
            isErr ? "#fca5a5" : open ? "#07a3b5" : "#e2e8f0"
          }`,
          borderRadius: 8,
          background: loading ? "#f8fafc" : "white",
          cursor: loading ? "not-allowed" : "pointer",
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          alignItems: "center",
          boxShadow: open ? "0 0 0 3px rgba(7,163,181,0.12)" : "none",
          transition: "all .15s",
        }}
      >
        {value ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "#07a3b5",
              color: "white",
              borderRadius: 20,
              padding: "2px 10px",
              fontSize: "0.78rem",
              fontWeight: 600,
              lineHeight: "22px",
            }}
          >
            {value}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: 0,
                color: "white",
              }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </button>
          </span>
        ) : (
          <span
            style={{
              fontSize: "0.85rem",
              color: loading ? "#b0bec5" : "#94a3b8",
              lineHeight: "28px",
            }}
          >
            {loading ? "Memuat opsi…" : placeholder}
          </span>
        )}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: "#94a3b8",
            marginLeft: "auto",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        />
      </div>
      {isErr && (
        <p style={{ margin: "3px 0 0", fontSize: "0.7rem", color: "#e74c3c" }}>
          Wajib dipilih
        </p>
      )}
      {open && !loading && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "white",
            border: "1.5px solid #e2e8f0",
            borderRadius: 10,
            boxShadow: "0 8px 30px rgba(0,0,0,.12)",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari…"
              style={{
                border: "none",
                outline: "none",
                fontSize: "0.82rem",
                width: "100%",
                fontFamily: "inherit",
                color: "#334155",
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            <button
              onClick={() => {
                onChange("");
                setOpen(false);
                setQ("");
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                border: "none",
                background: !value ? "#f0fdfe" : "white",
                cursor: "pointer",
                fontSize: "0.82rem",
                textAlign: "left",
                fontFamily: "inherit",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {!value && <CheckIcon sx={{ fontSize: 15, color: "#07a3b5" }} />}
              <em>— {placeholder} —</em>
            </button>
            {filtered.map((opt) => (
              <button
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setQ("");
                }}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "none",
                  background: value === opt ? "#f0fdfe" : "white",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  textAlign: "left",
                  fontFamily: "inherit",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {value === opt && (
                  <CheckIcon sx={{ fontSize: 15, color: "#07a3b5" }} />
                )}
                <span style={{ marginLeft: value === opt ? 0 : 23 }}>
                  {opt}
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 12, textAlign: "center", color: "#94a3b8", fontSize: "0.8rem" }}>
                Tidak ditemukan
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSearchSelect({
  label,
  options,
  value = [],
  onChange,
  required,
  placeholder = "Semua",
  hideSelectAll = false,
  singleSelect = false,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const filtered = options.filter((o) =>
    String(o).toLowerCase().includes(q.toLowerCase())
  );
  const safeValue = Array.isArray(value) ? value : [];
  const isErr = required && safeValue.length === 0;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (opt) => {
    if (singleSelect) {
      onChange([opt]);
      setOpen(false);
    } else {
      if (safeValue.includes(opt)) onChange(safeValue.filter((v) => v !== opt));
      else onChange([...safeValue, opt]);
    }
  };
  const selectAll = () => {
    if (singleSelect) return;
    onChange(safeValue.length === options.length ? [] : [...options]);
  };
  const remove = (opt, e) => {
    e.stopPropagation();
    if (singleSelect) return;
    onChange(safeValue.filter((v) => v !== opt));
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: isErr ? "#e74c3c" : "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        {required && <span style={{ color: "#e74c3c" }}> *</span>}
        {loading && <LoadingDots />}
      </div>
      <div
        onClick={() => !loading && setOpen((p) => !p)}
        style={{
          minHeight: 40,
          padding: "5px 10px",
          border: `1.5px solid ${
            isErr ? "#fca5a5" : open ? "#07a3b5" : "#e2e8f0"
          }`,
          borderRadius: 8,
          background: loading ? "#f8fafc" : "white",
          cursor: loading ? "not-allowed" : "pointer",
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          alignItems: "center",
          boxShadow: open ? "0 0 0 3px rgba(7,163,181,0.12)" : "none",
          transition: "all .15s",
        }}
      >
        {safeValue.length === 0 && (
          <span
            style={{
              fontSize: "0.85rem",
              color: loading ? "#b0bec5" : "#94a3b8",
              lineHeight: "28px",
            }}
          >
            {loading ? "Memuat opsi…" : placeholder}
          </span>
        )}
        {safeValue.map((v) => (
          <span
            key={v}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "#e0f7fa",
              color: "#07a3b5",
              borderRadius: 20,
              padding: singleSelect ? "2px 10px" : "2px 6px 2px 10px",
              fontSize: "0.78rem",
              fontWeight: 600,
              lineHeight: "22px",
            }}
          >
            {v}
            {!singleSelect && (
              <button
                onClick={(e) => remove(v, e)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                  color: "#07a3b5",
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </button>
            )}
          </span>
        ))}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: "#94a3b8",
            marginLeft: "auto",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        />
      </div>
      {isErr && (
        <p style={{ margin: "3px 0 0", fontSize: "0.7rem", color: "#e74c3c" }}>
          Wajib dipilih
        </p>
      )}
      {open && !loading && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "white",
            border: "1.5px solid #e2e8f0",
            borderRadius: 10,
            boxShadow: "0 8px 30px rgba(0,0,0,.12)",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari…"
              style={{
                border: "none",
                outline: "none",
                fontSize: "0.82rem",
                width: "100%",
                fontFamily: "inherit",
                color: "#334155",
              }}
              onClick={(e) => e.stopPropagation()}
            />
            {q && (
              <button
                onClick={() => setQ("")}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                }}
              >
                <CloseIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
              </button>
            )}
          </div>
          {!hideSelectAll && (
            <div
              style={{
                padding: "4px 6px 2px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <button
                onClick={selectAll}
                style={{
                  width: "100%",
                  padding: "7px 8px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  textAlign: "left",
                  fontFamily: "inherit",
                  color: "#475569",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: "1.5px solid",
                    borderColor:
                      safeValue.length === options.length ? "#07a3b5" : "#cbd5e1",
                    borderRadius: 4,
                    background:
                      safeValue.length === options.length ? "#07a3b5" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {safeValue.length === options.length && (
                    <CheckIcon sx={{ fontSize: 11, color: "white" }} />
                  )}
                </div>
                <em>Pilih Semua ({options.length})</em>
              </button>
            </div>
          )}
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.map((opt) => (
              <button
                key={opt}
                onClick={() => toggle(opt)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  textAlign: "left",
                  fontFamily: "inherit",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: "1.5px solid",
                    borderColor: safeValue.includes(opt) ? "#07a3b5" : "#cbd5e1",
                    borderRadius: 4,
                    background: safeValue.includes(opt) ? "#07a3b5" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {safeValue.includes(opt) && (
                    <CheckIcon sx={{ fontSize: 11, color: "white" }} />
                  )}
                </div>
                {opt}
              </button>
            ))}
            {filtered.length === 0 && (
              <div
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "0.8rem",
                }}
              >
                Tidak ditemukan
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterZone({ title, icon: Icon, children, rightSlot }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e8edf2",
        borderRadius: 10,
        padding: "16px 18px",
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: "#64748b" }} />}
          <span
            style={{ fontWeight: 700, color: "#374151", fontSize: "0.92rem" }}
          >
            {title}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {rightSlot}
        </div>
      </div>
      {children}
    </div>
  );
}

function TplModal({ open, mode, defaultName, onClose, onConfirm }) {
  const [name, setName] = useState("");
  useEffect(() => {
    if (open) setName(mode === "save" ? defaultName || "" : "");
  }, [open, mode, defaultName]);
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(0,0,0,.2)",
          padding: "28px 32px",
          width: 420,
          maxWidth: "92vw",
        }}
      >
        <h5
          style={{
            margin: "0 0 4px",
            fontWeight: 800,
            color: "#0f172a",
            fontSize: "1.05rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <SaveIcon sx={{ fontSize: 20, color: "#07a3b5" }} />{" "}
          {mode === "save" ? "Simpan Template" : "Simpan Sebagai Baru"}
        </h5>
        <p
          style={{ margin: "0 0 20px", color: "#64748b", fontSize: "0.82rem" }}
        >
          {mode === "save"
            ? "Perbarui template yang sedang aktif."
            : "Buat template baru dari konfigurasi saat ini."}
        </p>
        <label
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.6px",
          }}
        >
          Nama Template
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Cth: Analisis Jakarta Q1…"
          autoFocus
          onKeyDown={(e) =>
            e.key === "Enter" && name.trim() && onConfirm(name.trim())
          }
          style={{
            width: "100%",
            marginTop: 8,
            padding: "10px 12px",
            boxSizing: "border-box",
            border: "1.5px solid #e2e8f0",
            borderRadius: 8,
            fontSize: "0.88rem",
            outline: "none",
            fontFamily: "inherit",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#07a3b5")}
          onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 20,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 18px",
              border: "1.5px solid #e2e8f0",
              borderRadius: 8,
              background: "white",
              color: "#475569",
              cursor: "pointer",
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            Batal
          </button>
          <button
            onClick={() => name.trim() && onConfirm(name.trim())}
            style={{
              padding: "8px 20px",
              border: "none",
              borderRadius: 8,
              background: "#07a3b5",
              color: "white",
              cursor: "pointer",
              fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            {mode === "save" ? "Simpan" : "Buat Baru"}
          </button>
        </div>
      </div>
    </div>
  );
}

const makePivotF = () => ({
  DB: "",
  Fact: [],
  Market: [],
  Period: [],
  Level: ["1_Category"],
  Category: [],
  Brand: [],
  Product: [],
});

export default function ReportProjectedNewPivotData() {
  const accessToken = Cookies.get("access_token") || "";
  const navigate = useNavigate();
  const [dataProfile, setDataProfile] = useState({});

  const [tpls, setTpls] = useState([]);
  const [selTpl, setSelTpl] = useState("");
  const [modal, setModal] = useState({
    open: false,
    mode: "saveas",
    payload: {},
  });

  const [pf, setPf] = useState(makePivotF());
  const [appliedFilters, setAppliedFilters] = useState({
    Category: [],
    Brand: [],
    Product: [],
  });
  const [loadingDims, setLoadingDims] = useState({
    Fact: false,
    Period: false,
    Market: false,
    Category: false,
    Products: false,
  });
  const loadedDB = useRef("");
  const loadedLevel = useRef("");
  const loadedCat = useRef("");
  const [rawPivotData, setRawPivotData] = useState([]);
  const [pivotData, setPivotData] = useState([]);
  const [pivotState, setPivotState] = useState({
    rows: ["Group", "rpt_shortdesc"],
    cols: ["Market_Breakdown", "Period"],
    vals: ["FactValue"],
    aggregatorName: "Sum",
    rendererName: "Table",
  });
  const [busy, setBusy] = useState(false);
  const [loadProgress, setLoadProgress] = useState("");
  const [dims, setDims] = useState({
    DB: [],
    Fact: [],
    Period: [],
    Market: [],
    Level: [],
    Category: [],
    Brand: [],
    Product: [],
  });
  const [pdfBusy, setPdfBusy] = useState(false);

  const [masterCategories, setMasterCategories] = useState([]);
  const [masterBrands, setMasterBrands] = useState([]);
  const [masterProducts, setMasterProducts] = useState([]);

  useEffect(() => {
    fetchProfile(accessToken, setDataProfile, []);
  }, [accessToken]);

  // Load config templates from API on mount
  useEffect(() => {
    const loadTemplates = async () => {
      if (!accessToken) return;
      const templates = await fetchConfigTemplates(accessToken);
      setTpls(templates);
    };
    loadTemplates();
  }, [accessToken]);

  useEffect(() => {
    const loadDBs = async () => {
      const dbs = await fetchTypeProject(accessToken);
      setDims((p) => ({ ...p, DB: dbs }));
    };
    if (accessToken) loadDBs();
  }, [accessToken]);

  useEffect(() => {
    if (!pf.DB) return;

    const selectedLevels = pf.Level || [];
    const showCategory = selectedLevels.includes("1_Category") || selectedLevels.includes("3_Brand") || selectedLevels.includes("4_Product");
    const showBrand = selectedLevels.includes("3_Brand") || selectedLevels.includes("4_Product");
    const showProduct = selectedLevels.includes("4_Product");

    const levelKey = (pf.Level || []).slice().sort().join(",");
    const categoryKey = (pf.Category || []).slice().sort().join(",");
    const cacheKey = `${levelKey}|${categoryKey}`;
    if (cacheKey === loadedLevel.current) return;

    const needsCategory = showCategory;
    const needsProducts = showBrand || showProduct;

    if (!needsCategory && !needsProducts) {
      loadedLevel.current = cacheKey;
      return;
    }

    const loadMasterData = async () => {
      let cats = masterCategories;

      if (needsCategory && masterCategories.length === 0) {
        setLoadingDims((p) => ({ ...p, Category: true }));
        cats = await fetchCategoryMaster(accessToken);
        setMasterCategories(cats);
        setDims((p) => ({ ...p, Category: cats.map((c) => c.ord_category).filter(Boolean) }));
        setLoadingDims((p) => ({ ...p, Category: false }));
      }

      if (needsProducts) {
        if (categoryKey === loadedCat.current) {
          loadedLevel.current = cacheKey;
          return;
        }
        loadedCat.current = categoryKey;
        setLoadingDims((p) => ({ ...p, Products: true }));

        let prods = [];
        if (pf.Category?.length > 0) {
          const ids = pf.Category.map((val) => {
            const obj = cats.find((c) => c.ord_category === val || c.name === val);
            return obj ? obj.id : null;
          }).filter(Boolean);
          prods = ids.length > 0
            ? await fetchProductMaster(accessToken, { column_name: "category_master_id", column_operator: "IN", column_value: ids.join(",") })
            : await fetchProductMaster(accessToken);
        } else {
          prods = await fetchProductMaster(accessToken);
        }

        setMasterProducts(prods);
        setDims((p) => ({ ...p, Product: prods.map((pr) => pr.product_name).filter(Boolean) }));
        setLoadingDims((p) => ({ ...p, Products: false }));
      }

      loadedLevel.current = cacheKey;
    };

    loadMasterData();
  }, [pf.DB, pf.Level, pf.Category, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pf.DB || pf.DB === loadedDB.current) return;
    loadedDB.current = pf.DB;
    loadedLevel.current = "";
    loadedCat.current = "";

    setDims((p) => ({ ...p, Fact: [], Period: [], Market: [], Category: [], Brand: [], Product: [] }));
    setMasterCategories([]);
    setMasterProducts([]);
    setLoadingDims((p) => ({ ...p, Fact: true, Period: true, Market: true }));

    const loadFilterData = async () => {
      try {
        const [facts, periods, levels, markets] = await Promise.all([
          fetchFacts(accessToken, pf.DB),
          fetchPeriods(accessToken, pf.DB),
          fetchLevels(accessToken, pf.DB),
          fetchMarketBreakdowns(accessToken, pf.DB),
        ]);
        setDims((p) => ({
          ...p,
          Fact: facts,
          Period: periods,
          Level: levels,
          Market: markets,
        }));
      } catch (e) {
        console.error("Error loading filters", e);
      } finally {
        setLoadingDims((p) => ({ ...p, Fact: false, Period: false, Market: false }));
      }
    };
    loadFilterData();
  }, [pf.DB, accessToken]);

  const pivotValid = () =>
    pf.DB &&
    (Array.isArray(pf.Fact) ? pf.Fact.length > 0 : pf.Fact) &&
    (Array.isArray(pf.Market) ? pf.Market.length > 0 : pf.Market) &&
    (Array.isArray(pf.Period) ? pf.Period.length > 0 : pf.Period);

  const brandOpts = masterProducts.length
    ? [
        ...new Set(masterProducts.map((p) => p.brand_master)),
      ].filter(Boolean).sort()
    : dims.Brand;

  const prodOpts = (() => {
    let filtered = masterProducts;
    if (pf.Brand?.length) {
      filtered = filtered.filter((p) => pf.Brand.includes(p.brand_master));
    }
    return [...new Set(filtered.map((p) => p.product_name))].filter(Boolean).sort();
  })();

  const filteredPivotData = rawPivotData.filter((d) => {
    if (appliedFilters.Category?.length && !appliedFilters.Category.includes(d.Category)) return false;
    if (appliedFilters.Brand?.length && !appliedFilters.Brand.includes(d.Brand)) return false;
    if (appliedFilters.Product?.length && !appliedFilters.Product.includes(d.Product)) return false;
    return true;
  });

  function loadTpl(idx) {
    if (idx === "") return;
    const t = tpls.find((tpl) => tpl.id === idx);
    if (!t) return;
    try {
      const payload =
        typeof t.filter_payload === "string"
          ? JSON.parse(t.filter_payload)
          : t.filter_payload;
      const loadedPf = { ...makePivotF(), ...(payload.pf || {}) };
      if (loadedPf.Period && !Array.isArray(loadedPf.Period)) {
        loadedPf.Period = [loadedPf.Period];
      }
      if (loadedPf.Fact && !Array.isArray(loadedPf.Fact)) {
        loadedPf.Fact = [loadedPf.Fact];
      }
      if (loadedPf.Market && !Array.isArray(loadedPf.Market)) {
        loadedPf.Market = [loadedPf.Market];
      }
      setPf(loadedPf);
    } catch (e) {
      console.error("Error parsing template payload:", e);
    }
  }

  function openModal(page, mode, payload) {
    setModal({ open: true, mode, payload });
  }

  async function confirmModal(name) {
    const { mode, payload } = modal;
    if (mode === "save" && selTpl !== "") {
      const result = await updateConfigTemplate(
        accessToken,
        selTpl,
        name,
        payload
      );
      if (result) {
        const updated = await fetchConfigTemplates(accessToken);
        setTpls(updated);
      }
    } else {
      const result = await createConfigTemplate(accessToken, name, payload);
      if (result) {
        const updated = await fetchConfigTemplates(accessToken);
        setTpls(updated);
        setSelTpl(result.id);
      }
    }
    setModal((m) => ({ ...m, open: false }));
  }

  async function handleDeleteTpl() {
    if (!window.confirm("Hapus template ini?")) return;
    const ok = await deleteConfigTemplate(accessToken, selTpl);
    if (ok) {
      const updated = await fetchConfigTemplates(accessToken);
      setTpls(updated);
      setSelTpl("");
    }
  }
  async function generatePivot() {
    if (!pivotValid()) return;
    setBusy(true);
    setLoadProgress("Memuat halaman 1…");
    setAppliedFilters({
      Category: pf.Category,
      Brand: pf.Brand,
      Product: pf.Product,
    });
    try {
      const filterParams = {
        namagroup: pf.Category && pf.Category.length > 0 ? pf.Category : undefined,
        brand: pf.Brand && pf.Brand.length > 0 ? pf.Brand : undefined,
        product: pf.Product && pf.Product.length > 0 ? pf.Product : undefined,
      };

      // Fetch page 1
      const firstResult = await fetchRawDataPage(accessToken, {
        db_set: pf.DB,
        fact: pf.Fact,
        period: pf.Period,
        market_breakdown: pf.Market,
        level: pf.Level,
        size: 100,
        page: 1,
        ...filterParams,
      });
      let allData = [...(firstResult.data || [])];
      const pagination = firstResult.pagination_info;

      if (pagination && pagination.total_pages > 1) {
        const totalPages = pagination.total_pages;
        for (let pg = 2; pg <= totalPages; pg++) {
          setLoadProgress(`Memuat halaman ${pg}/${totalPages}…`);
          const pageResult = await fetchRawDataPage(accessToken, {
            db_set: pf.DB,
            fact: pf.Fact,
            period: pf.Period,
            market_breakdown: pf.Market,
            level: pf.Level,
            size: 100,
            page: pg,
            ...filterParams,
          });
          if (pageResult.data && pageResult.data.length > 0) {
            allData = [...allData, ...pageResult.data];
          }
        }
      }

      setRawPivotData(allData);

      setPivotState((p) => ({ ...p, rows: ["Group", "rpt_shortdesc"] }));
      setLoadProgress("");
    } catch (error) {
      console.error("Error generating pivot:", error);
      setLoadProgress("");
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf() {
    const el = document.getElementById("pivotWrap");
    if (!el || filteredPivotData.length === 0) return;
    setPdfBusy(true);

    // Save original scroll and layout styles
    const originalMaxHeight = el.style.maxHeight;
    const originalOverflowY = el.style.overflowY;
    const originalOverflowX = el.style.overflowX;

    // Temporarily expand to full height and width to render all scrollable content in PDF
    el.style.maxHeight = "none";
    el.style.overflowY = "visible";
    el.style.overflowX = "visible";

    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: el.scrollWidth,
        height: el.scrollHeight,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });

      // Restore original scroll and layout styles immediately
      el.style.maxHeight = originalMaxHeight;
      el.style.overflowY = originalOverflowY;
      el.style.overflowX = originalOverflowX;

      const imgData = canvas.toDataURL("image/png");
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // original CSS width of the element
      const pdfPageWidth = el.scrollWidth;
      const scale = pdfPageWidth / canvasWidth;
      const imgHeightScaled = canvasHeight * scale;

      const isLandscape = canvasWidth > canvasHeight;
      const pdfPageHeight = isLandscape ? pdfPageWidth * 0.7 : pdfPageWidth * 1.3;

      const orientation = isLandscape ? "landscape" : "portrait";
      const pdf = new jsPDF({
        orientation,
        unit: "px",
        format: [pdfPageWidth, pdfPageHeight],
      });

      let yOffset = 0;
      while (yOffset < imgHeightScaled) {
        if (yOffset > 0) {
          pdf.addPage([pdfPageWidth, pdfPageHeight], orientation);
        }
        pdf.addImage(imgData, "PNG", 0, -yOffset, pdfPageWidth, imgHeightScaled);
        yOffset += pdfPageHeight;
      }

      const title = `Report Projected ${pf.DB} - ${pf.Fact}`;
      pdf.save(`${title}.pdf`);
    } catch (error) {
      // Restore layout styles in case of error
      el.style.maxHeight = originalMaxHeight;
      el.style.overflowY = originalOverflowY;
      el.style.overflowX = originalOverflowX;
      console.error("Error generating PDF:", error);
    } finally {
      setPdfBusy(false);
    }
  }

  function downloadExcel() {
    const table = document.querySelector("#pivotWrap table");
    if (!table || filteredPivotData.length === 0) return;
    try {
      const clone = table.cloneNode(true);
      const classesToRemove = [".pvtTotal", ".pvtTotalLabel", ".pvtGrandTotal"];
      classesToRemove.forEach((cls) => {
        clone.querySelectorAll(cls).forEach((el) => el.remove());
      });
      clone.querySelectorAll("tr").forEach((tr) => {
        if (tr.children.length === 0) {
          tr.remove();
        }
      });
      const wb = XLSX.utils.table_to_book(clone);
      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      
      // Autofit column widths
      if (ws && ws["!ref"]) {
        const range = XLSX.utils.decode_range(ws["!ref"]);
        const colWidths = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxWidth = 10;
          for (let R = range.s.r; R <= range.e.r; ++R) {
            const cell_address = { c: C, r: R };
            const cell_ref = XLSX.utils.encode_cell(cell_address);
            const cell = ws[cell_ref];
            if (cell && cell.v !== undefined) {
              const cellText = String(cell.v);
              const len = cellText.length;
              if (len > maxWidth) {
                maxWidth = len;
              }
            }
          }
          colWidths.push({ wch: Math.min(maxWidth + 4, 50) });
        }
        ws["!cols"] = colWidths;
      }

      const filename = `Report_Projected_Pivot_${pf.DB}_${pf.Fact}_${pf.Period}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error generating Excel:", error);
    }
  }

  function reset() {
    setPf(makePivotF());
    setRawPivotData([]);
    setAppliedFilters({
      Category: [],
      Brand: [],
      Product: [],
    });
  }

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        .hdr-btn:hover { opacity: 0.85; }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        .pvtTable {
          border-collapse: collapse !important;
          border: 1.5px solid #068b9a !important;
          width: 100% !important;
        }
        .pvtTable thead tr th, .pvtTable tbody tr th {
          background: #07a3b5 !important;
          color: white !important;
          font-weight: 700;
          padding: 10px 14px;
          font-size: 0.8rem;
          border: 1px solid rgba(255,255,255,0.25) !important;
        }
        .pvtTable tbody tr td {
          padding: 10px 14px;
          font-size: 0.8rem;
          border: 1px solid #e2e8f0 !important;
          color: #334155 !important;
        }
        .pvtTable tbody tr td.pvtTotal { font-weight: 700; color: #07a3b5 !important; background: #f0fdfe !important; }
        .pvtTable tbody tr:hover td { background: #f0fdfe !important; }
        .pvtAxisContainer li span.pvtAttr { background: white !important; border: 1.5px solid #07a3b5 !important; border-radius: 20px !important; padding: 4px 14px !important; font-size: 0.8rem !important; font-weight: 600 !important; color: #07a3b5 !important; cursor: default !important; margin: 3px; }
        .pvtAxisContainer li span.pvtAttr .pvtTriangle { cursor: pointer !important; }
        select.pvtRenderer, select.pvtAggregator { border: 1.5px solid #e2e8f0 !important; border-radius: 8px !important; padding: 6px 10px !important; font-size: 0.8rem !important; outline: none !important; width: 100% !important; margin-bottom: 8px !important; }
        .pvtFilterBox { border: 1px solid #e2e8f0 !important; border-radius: 12px !important; box-shadow: 0 8px 30px rgba(0,0,0,.12) !important; background: white !important; z-index: 9999 !important; padding: 12px !important; }
        .pvtCheckContainer { max-height: 240px !important; overflow-y: auto !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .pvtTable .pvtTotal,
        .pvtTable .pvtTotalLabel,
        .pvtTable .pvtGrandTotal {
          display: none !important;
        }
        .pvtTable tbody tr:last-child {
          display: none !important;
        }
      `}</style>

      <Navbar
        active="Projected Reporting"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />

      <TplModal
        open={modal.open}
        mode={modal.mode}
        defaultName={
          selTpl !== "" ? tpls.find((t) => t.id === selTpl)?.template_name : ""
        }
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={confirmModal}
      />

      <div style={{ background: "#f0f4f8", minHeight: "calc(100vh - 64px)" }}>
        <div
          style={{ maxWidth: "100%", margin: "0 auto", padding: "16px 16px" }}
        >
          {/* Navigation Buttons Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 2,
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 4,
                boxShadow: "0 1px 6px rgba(0,0,0,.06)",
              }}
            >
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 28px",
                  borderRadius: 9,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  fontFamily: "inherit",
                  background: "#07a3b5",
                  color: "white",
                  boxShadow: "0 3px 10px rgba(7,163,181,0.3)",
                  transition: "all .18s",
                }}
              >
                <GridViewIcon sx={{ fontSize: 18 }} /> Pivot Report View
              </button>
              <button
                onClick={() => navigate("/projected-reporting-tabular")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 28px",
                  borderRadius: 9,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.88rem",
                  fontFamily: "inherit",
                  background: "transparent",
                  color: "#64748b",
                  transition: "all .18s",
                }}
              >
                <TableChartIcon sx={{ fontSize: 18 }} /> Tabular Report View
              </button>
              <button
                onClick={() => navigate("/projected-reporting")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 28px",
                  borderRadius: 9,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.88rem",
                  fontFamily: "inherit",
                  background: "transparent",
                  color: "#64748b",
                  transition: "all .18s",
                }}
              >
                <BarChartIcon sx={{ fontSize: 18 }} /> Custom Chart Report View
              </button>
            </div>
          </div>

          {/* Pivot View Content */}
          <div
            style={{
              background: "white",
              borderRadius: 12,
              boxShadow: "0 1px 8px rgba(0,0,0,.06)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#07a3b5",
                padding: "13px 22px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <GridViewIcon sx={{ fontSize: 20, color: "white" }} />
                <span
                  style={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.98rem",
                  }}
                >
                  Premium Projected Pivot
                </span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={downloadPdf}
                  disabled={pdfBusy || filteredPivotData.length === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 16px",
                    border: "1.5px solid rgba(255,255,255,0.5)",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.15)",
                    color: "white",
                    cursor:
                      filteredPivotData.length > 0 && !pdfBusy
                        ? "pointer"
                        : "not-allowed",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    fontFamily: "inherit",
                    opacity: filteredPivotData.length > 0 ? 1 : 0.5,
                    transition: "all .15s",
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 16 }} />{" "}
                  {pdfBusy ? "Generating…" : "PDF"}
                </button>
                <button
                  onClick={downloadExcel}
                  disabled={filteredPivotData.length === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 16px",
                    border: "1.5px solid rgba(255,255,255,0.5)",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.15)",
                    color: "white",
                    cursor: filteredPivotData.length > 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    fontFamily: "inherit",
                    opacity: filteredPivotData.length > 0 ? 1 : 0.5,
                    transition: "all .15s",
                  }}
                >
                  <GridOnIcon sx={{ fontSize: 16 }} /> Excel
                </button>
              </div>
            </div>

            <div style={{ padding: 22 }}>
              {/* Template Bar */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  alignItems: "center",
                  padding: "12px 16px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  marginBottom: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <BookmarkIcon sx={{ fontSize: 17, color: "#f59e0b" }} />
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#475569",
                      fontSize: "0.82rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Template Pivot:
                  </span>
                  <select
                    value={selTpl}
                    onChange={(e) => {
                      setSelTpl(e.target.value);
                      loadTpl(e.target.value);
                    }}
                    style={{
                      fontSize: "0.82rem",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "6px 10px",
                      background: "white",
                      minWidth: 160,
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                  >
                    <option value="">— Bawaan Pabrik —</option>
                    {tpls.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.template_name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() =>
                      openModal("pivot", "save", { pf })
                    }
                    style={iconBtn("#07a3b5")}
                    title="Simpan"
                  >
                    <SaveIcon sx={{ fontSize: 16 }} />
                  </button>
                  {selTpl !== "" && (
                    <button
                      onClick={handleDeleteTpl}
                      style={iconBtn("#e74c3c")}
                      title="Hapus"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Zone */}
              <FilterZone
                title="Filter Analitik Table"
                icon={FilterAltIcon}
                rightSlot={
                  <>
                    <button
                      onClick={reset}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        border: "1.5px solid #e2e8f0",
                        background: "white",
                        borderRadius: 8,
                        padding: "7px 14px",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        color: "#475569",
                        fontFamily: "inherit",
                      }}
                    >
                      <RestartAltIcon sx={{ fontSize: 16 }} /> Reset
                    </button>
                    <button
                      onClick={generatePivot}
                      disabled={!pivotValid() || busy}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        border: "none",
                        borderRadius: 8,
                        padding: "7px 18px",
                        cursor:
                          pivotValid() && !busy ? "pointer" : "not-allowed",
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        fontFamily: "inherit",
                        background:
                          pivotValid() && !busy ? "#07a3b5" : "#e2e8f0",
                        color: pivotValid() && !busy ? "white" : "#94a3b8",
                        boxShadow:
                          pivotValid() && !busy
                            ? "0 3px 10px rgba(7,163,181,0.3)"
                            : "none",
                        transition: "all .2s",
                      }}
                    >
                      <AutoFixHighIcon sx={{ fontSize: 16 }} />{" "}
                      {busy ? "Loading…" : "Terapkan"}
                    </button>
                  </>
                }
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 14,
                    marginBottom: 14,
                  }}
                >
                  <SearchSelect
                    label="DB Set Name"
                    options={dims.DB}
                    value={pf.DB}
                    onChange={(v) =>
                      setPf((p) => ({ ...makePivotF(), DB: v }))
                    }
                    required
                  />
                  <MultiSearchSelect
                    label="Fact"
                    options={dims.Fact}
                    value={pf.Fact}
                    loading={loadingDims.Fact}
                    onChange={(v) => setPf((p) => ({ ...p, Fact: v }))}
                    required
                  />
                  <MultiSearchSelect
                    label="Period"
                    options={dims.Period}
                    value={pf.Period}
                    loading={loadingDims.Period}
                    onChange={(v) => setPf((p) => ({ ...p, Period: v }))}
                    required
                  />
                  <MultiSearchSelect
                    label="Market"
                    options={dims.Market}
                    value={pf.Market}
                    loading={loadingDims.Market}
                    onChange={(v) => setPf((p) => ({ ...p, Market: v }))}
                    required
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${
                      1 +
                      (pf.Level?.includes("1_Category") || pf.Level?.includes("3_Brand") || pf.Level?.includes("4_Product") ? 1 : 0) +
                      (pf.Level?.includes("3_Brand") || pf.Level?.includes("4_Product") ? 1 : 0) +
                      (pf.Level?.includes("4_Product") ? 1 : 0)
                    }, 1fr)`,
                    gap: 14,
                  }}
                >
                  <MultiSearchSelect
                    label="Level"
                    options={LEVEL_OPTION}
                    value={pf.Level}
                    onChange={(v) => {
                      setPf((p) => {
                        const newPf = { ...p, Level: v };
                        const hasBrand = v.includes("3_Brand") || v.includes("4_Product");
                        const hasProduct = v.includes("4_Product");
                        if (!hasBrand) {
                          newPf.Brand = [];
                          newPf.Product = [];
                        } else if (!hasProduct) {
                          newPf.Product = [];
                        }
                        return newPf;
                      });
                    }}
                  />
                  {pf.Level && (pf.Level.includes("1_Category") || pf.Level.includes("3_Brand") || pf.Level.includes("4_Product")) && (
                    <MultiSearchSelect
                      label="Category"
                      options={dims.Category}
                      value={pf.Category}
                      loading={loadingDims.Category}
                      onChange={(v) =>
                        setPf((p) => ({
                          ...p,
                          Category: v,
                          Brand: [],
                          Product: [],
                        }))
                      }
                    />
                  )}
                  {pf.Level && (pf.Level.includes("3_Brand") || pf.Level.includes("4_Product")) && (
                    <MultiSearchSelect
                      label="Brand"
                      options={brandOpts}
                      value={pf.Brand}
                      loading={loadingDims.Products}
                      onChange={(v) =>
                        setPf((p) => ({ ...p, Brand: v, Product: [] }))
                      }
                    />
                  )}
                  {pf.Level && pf.Level.includes("4_Product") && (
                    <MultiSearchSelect
                      label="Product"
                      options={prodOpts}
                      value={pf.Product}
                      loading={loadingDims.Products}
                      onChange={(v) => setPf((p) => ({ ...p, Product: v }))}
                    />
                  )}
                </div>
              </FilterZone>

              {/* Loading progress bar */}
              {busy && loadProgress && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "#eff8ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 8,
                    padding: "10px 16px",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      border: "2.5px solid #e2e8f0",
                      borderTop: "2.5px solid #07a3b5",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#1e40af",
                      fontWeight: 600,
                    }}
                  >
                    {loadProgress}
                  </span>
                </div>
              )}

              <div
                id="pivotWrap"
                style={{
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: 650,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                }}
              >
                {rawPivotData.length > 0 ? (
                  <PivotTable
                    data={filteredPivotData}
                    {...pivotState}
                  />
                ) : (
                  <div
                    style={{
                      padding: 60,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    <GridViewIcon
                      sx={{ fontSize: 44, opacity: 0.12, mb: 1.5 }}
                    />
                    <p style={{ fontWeight: 700, margin: 0 }}>
                      Pivot belum digenerate
                    </p>
                    <p style={{ fontSize: "0.82rem", marginTop: 6 }}>
                      Pilih <b style={{ color: "#e74c3c" }}>DB Set</b> dan{" "}
                      <b style={{ color: "#e74c3c" }}>Fact</b>, lalu klik{" "}
                      <b>Generate Pivot</b>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
